import React, { useCallback, useMemo, useState } from "react";

import styled, { CSSProperties } from "styled-components";
import { v4 as uuid } from "uuid";
import { titleCase } from "title-case";
import { CrossIcon, LockIcon } from "../Images/icons";
import DateInput from "./DateInput";
import PhoneInput from "./Phone";
import "text-security";
import ErrorWrapper from "./ErrorWrapper";
import { OptionalFunction, ReactSelectOptions } from "../types";
import { PasswordInput } from "./PasswordInput";
import { FlexContainer } from "../Containers";
import { ReactComponent as EyeShownIcon } from "../Images/icons/EyeShown.svg";
import { ReactComponent as EyeHiddenIcon } from "../Images/icons/EyeHidden.svg";
import { ColorVariables } from "../Colors";
import { Dropdown } from "./Dropdowns";
import Chevron, { ChevronOrientation } from "../Images/icons/Chevron";
import IntlPhoneInput from "./IntlPhoneInput";

const StyledActionIcon = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  margin-right: 14px;
  margin-top: 18px;
  svg {
    cursor: pointer;
    width: 12px;
    height: 12px;
    &:hover {
      opacity: 0.8;
    }
  }
`;

const StyledCancel = styled(StyledActionIcon)<{ $showClear?: boolean }>`
  visibility: ${(props) => (props.$showClear ? "visible" : "hidden")};
`;

const StyledEyeIcon = styled.button<{ $show: boolean }>`
  border: 0;
  padding: 0;
  width: 24px;
  height: 18px;
  margin-left: 16px;
  svg {
    fill: ${(props) => (props.$show ? ColorVariables.mint : ColorVariables.mint7)};
    width: 24px;
    height: 18px;
  }
`;

export const convertStringListToOptions = (val: string[]): ReactSelectOptions => {
  return val.map((item) => {
    return {
      id: item,
      label: titleCase(item.toLowerCase()),
      value: item,
    };
  });
};

const ShowHideEye = (props: { show: boolean; onToggle: () => any }) => {
  const { show, onToggle } = props;
  return (
    <StyledEyeIcon tabIndex={-1} $show={show} type="button" onClick={onToggle}>
      {show ? <EyeShownIcon /> : <EyeHiddenIcon />}
    </StyledEyeIcon>
  );
};

export type InputProps = Partial<{
  creatable: boolean;
  cancellable: boolean;
  autoComplete: boolean | string; // Can either be true / false / "someFieldName"
  title: string;
  type:
    | "autocomplete"
    | "select"
    | "text"
    | "phone"
    | "date"
    | "password"
    | "number"
    | "textarea"
    | "date-range"
    | "phone-intl";
  showLock: boolean;
  onFocus: OptionalFunction;
  options?: ReactSelectOptions;
  value: string;
  onChange: OptionalFunction;
  handleBlur: OptionalFunction;
  placeholder: string;
  error: boolean;
  caption: string;
  captionColor: string;
  captionAlign: string;
  onCaptionClick: OptionalFunction;
  readOnly?: boolean;
  required: boolean;
  handleKeyDown: OptionalFunction;
  className: string;
  usePasswordAsText: boolean;
  helperTitle: string;
  subtitle: string;
  helperBody: JSX.Element;
  style?: CSSProperties;
  maxDate?: Date;
  minDate?: Date;
  isSmall?: boolean;
  min?: number;
  max?: number;
  dropdownOptions?: {
    menuPlacement?: "top" | "bottom" | "auto";
  };
  errorMessagePosition?: "top left" | "top right" | "top center" | "bottom center" | "bottom left";
  "bottom right";
}> & {
  name: string;
  showEnterArrow?: boolean;
  handleEnter?: () => void;
  saveInputOnBlur?: boolean;
  dropdownIndicator?: boolean;
  id?: string;
  titleColor?: string;
};

const Input = ({
  name,
  creatable = false,
  cancellable = true,
  readOnly = false,
  autoComplete = false,
  options = [],
  subtitle = "",
  title = "",
  type = "text",
  showLock = false,
  value = "",
  onChange = undefined,
  onFocus = undefined,
  handleBlur = undefined,
  placeholder = "",
  error = false,
  caption = "",
  captionColor = "",
  captionAlign = "flex-start",
  onCaptionClick = undefined,
  required = false,
  handleKeyDown = undefined,
  className = "",
  usePasswordAsText = false,
  helperBody = undefined,
  helperTitle = undefined,
  style = {},
  min = undefined,
  max = undefined,
  maxDate = undefined,
  minDate = undefined,
  isSmall = false,
  dropdownOptions = undefined,
  errorMessagePosition = "bottom left",
  showEnterArrow = false,
  handleEnter = () => {},
  saveInputOnBlur = false,
  dropdownIndicator = true,
  id = undefined,
  titleColor = undefined,
}: InputProps) => {
  const [active, setActive] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const isCancellable = cancellable && !readOnly && ["date", "select"].indexOf(type) === -1;

  const newId = useMemo(() => {
    return uuid();
  }, [name]);

  const autoCompleteValue = useMemo(() => {
    if (typeof autoComplete === "string") {
      if (autoComplete) {
        return autoComplete;
      }
      return "on";
    }
    return `id-${new Date().getTime().toString()}`;
  }, [name, autoComplete]);

  const toggleHighlight = useCallback(() => {
    setActive(!active);
  }, [active, setActive]);

  const handleOnDateChange = useCallback(
    (date) => {
      if (onChange) {
        onChange(date);
      }
    },
    [onChange]
  );

  const handleOnPhoneChange = useCallback(
    (val) => {
      if (onChange) {
        onChange(val);
      }
    },
    [onChange]
  );

  const handleOnChange = useCallback(
    (e) => {
      if (onChange) {
        if (type === "number") {
          onChange(parseInt(e.target.value));
        } else {
          onChange(e.target.value);
        }
      }
    },
    [onChange]
  );

  const handleOnFocus = useCallback(() => {
    if (!readOnly) {
      toggleHighlight();
    }
    if (onFocus) {
      onFocus();
    }
  }, [readOnly, onFocus, toggleHighlight]);

  const handleOnBlur = useCallback(() => {
    if (!readOnly) {
      toggleHighlight();
    }
    if (handleBlur) {
      handleBlur();
    }
  }, [readOnly, handleBlur, toggleHighlight]);

  const handleOnKeyDown = useCallback(
    (e) => {
      if (handleKeyDown) {
        handleKeyDown(e.key);
      }
    },
    [handleKeyDown]
  );

  const handleCancel = useCallback(() => {
    if (onChange) {
      onChange("");
    }
  }, [onChange]);

  const computedClassNames = useMemo(() => {
    const classNames: string[] = [];
    if (className) classNames.push(className);
    if (isSmall) classNames.push("isSmall");
    return classNames.join(" ");
  }, [isSmall]);

  const getInputToDisplay = () => {
    if (type === "phone-intl") {
      return <IntlPhoneInput name={name} value={value} onChange={handleOnPhoneChange} />;
    }
    if (type === "phone") {
      return (
        <PhoneInput
          autoComplete={autoComplete}
          value={value}
          onChange={handleOnPhoneChange}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          onKeyDown={handleOnKeyDown}
          name={name}
        />
      );
    }
    if (type === "date") {
      return (
        <DateInput
          autoComplete={autoComplete}
          id={id || newId}
          name={name}
          maxDate={maxDate}
          minDate={minDate}
          value={value}
          onChange={handleOnDateChange}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          onKeyDown={handleOnKeyDown}
          data-name={name}
        />
      );
    }
    if (type === "password") {
      return (
        <PasswordInput
          autoComplete={autoCompleteValue}
          id={newId}
          name={name}
          value={value}
          usePasswordAsText={usePasswordAsText}
          onChange={handleOnChange}
          placeholder={placeholder}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          onKeyDown={handleOnKeyDown}
          showValue={showPassword}
          data-name={name}
        />
      );
    }
    if (type === "select") {
      return (
        <Dropdown
          isSmall={isSmall}
          name={name}
          error={error}
          creatable={creatable}
          cancellable={cancellable}
          options={options}
          value={value}
          onChange={(val) =>
            handleOnChange({
              target: {
                value: val ? val.value : null,
              },
            })
          }
          dropdownOptions={dropdownOptions}
          saveInputOnBlur={saveInputOnBlur}
          placeholder={placeholder || ""}
          dropdownIndicator={dropdownIndicator}
        />
      );
    }
    if (type === "textarea") {
      return (
        <textarea
          autoComplete={autoCompleteValue}
          id={newId}
          name={name}
          readOnly={readOnly}
          onChange={handleOnChange}
          placeholder={placeholder}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          onKeyDown={handleOnKeyDown}
          data-lpignore={autoComplete ? undefined : "true"}
          data-name={name}
          value={value}
        />
      );
    }
    return (
      <input
        autoComplete={autoCompleteValue}
        className={computedClassNames}
        id={newId}
        type={type}
        name={name}
        min={min}
        max={max}
        value={value}
        readOnly={readOnly}
        onChange={handleOnChange}
        placeholder={placeholder}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onKeyDown={handleOnKeyDown}
        data-lpignore={autoComplete ? undefined : "true"}
        data-name={name}
      />
    );
  };

  return (
    <ErrorWrapper
      active={active}
      title={title}
      subtitle={subtitle}
      showLock={showLock}
      error={error}
      caption={caption}
      captionColor={captionColor}
      captionAlign={captionAlign}
      name={name}
      onCaptionClick={onCaptionClick}
      required={required}
      className={className}
      type={type}
      data-name="errorWrapper"
      helperTitle={helperTitle}
      helperBody={helperBody}
      errorMessagePosition={errorMessagePosition}
      titleColor={titleColor}
    >
      <FlexContainer $alignItems="center" {...style}>
        <div className="full-width relative" data-name="inputContainer">
          {getInputToDisplay()}
          {isCancellable && (
            <StyledCancel data-name="cancellable" onClick={handleCancel} $showClear={value?.toString().length >= 0}>
              <CrossIcon />
            </StyledCancel>
          )}
          {readOnly && (
            <StyledActionIcon data-name="lockIcon">
              <LockIcon />
            </StyledActionIcon>
          )}
          {showEnterArrow && (
            <StyledCancel onClick={handleEnter} $showClear={value?.toString().length >= 0}>
              <Chevron orientation={ChevronOrientation.RIGHT} />
            </StyledCancel>
          )}
        </div>
        {type === "password" && (
          <ShowHideEye
            data-name="passwordHideEye"
            onToggle={() => setShowPassword(!showPassword)}
            show={showPassword}
          />
        )}
      </FlexContainer>
    </ErrorWrapper>
  );
};

export { Input };
